import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { RestorePanelButton, usePanelReturn } from './PanelReturn';
import { cn, focusRing } from './cn';

export interface Crumb {
  label: ReactNode;
  /** Omit for the current page. */
  to?: string;
}

/** The trail from a list to the record being viewed: Rides › Ride 901. */
export function Breadcrumbs({ items }: { items: Crumb[] }) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t('breadcrumb')}>
      <ol className="m-0 flex list-none flex-wrap items-center gap-1 p-0 text-sm text-ink-muted">
        {items.map((c, i) => (
          <li key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight size={12} aria-hidden className="text-ink-faint" />}
            {c.to ? (
              <Link to={c.to} className={cn('rounded-sm text-ink-muted no-underline hover:text-accent hover:underline', focusRing)}>
                {c.label}
              </Link>
            ) : (
              <span aria-current="page" className="text-ink">
                {c.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export interface PageHeaderProps {
  title: ReactNode;
  /** Status badges next to the title. */
  badges?: ReactNode;
  /** One line under the title. */
  description?: ReactNode;
  breadcrumbs?: Crumb[];
  /** The page's actions, primary last; they wrap under the title on narrow screens. */
  actions?: ReactNode;
  className?: string;
}

/** The top of every page: breadcrumb, title (the page's only h1), badges and actions. */
export function PageHeader({ title, badges, description, breadcrumbs, actions, className }: PageHeaderProps) {
  const origin = usePanelReturn();
  return (
    <header className={cn('mb-4 flex flex-col gap-1.5', className)}>
      {breadcrumbs && breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} />}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* the title keeps room for itself; actions that don't fit beside it move to the next row */}
        <div className="flex min-w-[min(100%,12rem)] flex-1 flex-wrap items-center gap-x-2.5 gap-y-1">
          <h1 className="m-0 font-display text-2xl font-semibold text-heading">{title}</h1>
          {badges}
        </div>
        {(actions || origin) && <div className="flex flex-wrap items-center gap-2"><RestorePanelButton origin={origin} />{actions}</div>}
      </div>
      {description && <p className="m-0 text-sm text-ink-muted">{description}</p>}
    </header>
  );
}
